/**
 * Lectura síncrona de sesión Supabase en localStorage.
 * Evita contención del lock de GoTrue (AbortError) en arranque post-login.
 */

import type { Session, User } from '@supabase/supabase-js';
import { buildUserFromAccessToken } from './persistSupabaseSession';
import { SUPABASE_STORAGE_KEY } from './supabaseClient';

export function isAuthAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; message?: string };
  return (
    e.name === 'AbortError' ||
    (typeof e.message === 'string' && e.message.toLowerCase().includes('abort'))
  );
}

/**
 * "Already used" es distinto de un refresh token genuinamente inválido:
 * pasa cuando dos llamadas a refreshSession() salen casi al mismo tiempo
 * (ej. varios guards verificando la sesión por separado) — la primera
 * rota el token con éxito, la segunda llega tarde con el token viejo y
 * Supabase la rechaza. La sesión sigue siendo válida, solo hay que volver
 * a leerla en vez de tratarlo como una sesión muerta.
 */
export function isRefreshTokenRaceError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; message?: string; code?: string };
  const msg = (e.message || '').toLowerCase();
  return (
    e.name === 'AuthApiError' && (
      msg.includes('already used')
      || e.code === 'refresh_token_already_used'
    )
  );
}

export function isInvalidRefreshTokenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; message?: string; code?: string };
  const msg = (e.message || '').toLowerCase();
  if (isRefreshTokenRaceError(error)) return false;
  return (
    e.name === 'AuthApiError' && (
      msg.includes('invalid refresh token')
      || msg.includes('refresh token not found')
      || e.code === 'refresh_token_not_found'
    )
  );
}

export interface StoredAuthSnapshot {
  hasToken: boolean;
  accessToken: string | null;
  user: User | null;
  session: Session | null;
}

export function readStoredAuthSnapshot(): StoredAuthSnapshot {
  const empty: StoredAuthSnapshot = {
    hasToken: false,
    accessToken: null,
    user: null,
    session: null,
  };

  if (typeof window === 'undefined') return empty;

  try {
    const raw = localStorage.getItem(SUPABASE_STORAGE_KEY);
    if (!raw) return empty;

    const data = JSON.parse(raw) as {
      access_token?: string;
      refresh_token?: string;
      user?: User;
      expires_at?: number;
      expires_in?: number;
    };

    const accessToken = data.access_token ?? null;
    if (!accessToken) return empty;

    let user =
      data.user && typeof data.user === 'object' ? data.user : null;
    if (!user) {
      user = buildUserFromAccessToken(accessToken);
    }

    return {
      hasToken: true,
      accessToken,
      user,
      session: data as unknown as Session,
    };
  } catch {
    return empty;
  }
}

export function hasStoredAuthToken(): boolean {
  return readStoredAuthSnapshot().hasToken;
}
